// Auth store — the single source of truth for "is the user logged in?".
//
// The access/refresh tokens themselves live in localStorage (see lib/tokens.ts).
// This store mirrors the token state in memory so React components can react
// to login/logout without polling localStorage.

import { create } from "zustand";
import { api } from "@/lib/api";
import { clearTokens, getAccessToken, setTokens } from "@/lib/tokens";
import type { CurrentUser } from "@/types/auth";

interface AuthState {
  accessToken: string | null;
  user: CurrentUser | null;
  loading: boolean;
  hydrated: boolean;

  /** Called once at app startup to check for an existing session. */
  hydrate: () => Promise<void>;
  /** Record a successful login and fetch the user profile. */
  setSession: (accessToken: string, refreshToken: string) => Promise<void>;
  /** Refresh the cached user profile (after profile updates, etc.). */
  refreshUser: () => Promise<void>;
  /** Clear tokens and in-memory state. */
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: getAccessToken(),
  user: null,
  loading: false,
  hydrated: false,

  hydrate: async () => {
    const token = getAccessToken();
    if (!token) {
      set({ hydrated: true });
      return;
    }
    set({ loading: true });
    try {
      const user = await api<CurrentUser>("/auth/me");
      set({ user, accessToken: token, loading: false, hydrated: true });
    } catch {
      clearTokens();
      set({ accessToken: null, user: null, loading: false, hydrated: true });
    }
  },

  setSession: async (accessToken, refreshToken) => {
    // Persist tokens to localStorage first so the api() helper picks
    // them up via getAccessToken() when fetching /auth/me below.
    setTokens(accessToken, refreshToken);
    set({ loading: true });
    try {
      const user = await api<CurrentUser>("/auth/me");
      // Atomic publish: set accessToken + user in the SAME `set()`.
      // The previous version set accessToken first, fetched /auth/me,
      // then set user — during the gap, route guards saw
      // (accessToken=NEW, user=null) and fired premature redirects
      // (e.g. RedirectIfAuthed → Navigate to="/app" beat the caller's
      // own Navigate to "/verify-email-required").
      set({ accessToken, user, loading: false });
    } catch (e) {
      clearTokens();
      set({ accessToken: null, user: null, loading: false });
      throw e;
    }
  },

  refreshUser: async () => {
    if (!get().accessToken) return;
    const user = await api<CurrentUser>("/auth/me");
    set({ user });
  },

  logout: () => {
    clearTokens();
    set({ accessToken: null, user: null });
  },
}));
